import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import './Header.css';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const navigate = useNavigate();

  // Use new auth context
  const { isAuthenticated, user, signOut, signInWithTwitter, connectWallet, isConnecting } = useAuthContext();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    closeMenu();
    window.scrollTo(0, 0);
  };

  const handleSignOutClick = () => {
    setShowSignOutModal(true);
  };

  const confirmSignOut = async () => {
    setShowSignOutModal(false);
    closeMenu();
    await signOut();
    // Clear old localStorage too for clean migration
    localStorage.removeItem('twitterUser');
    window.location.reload();
  };

  const cancelSignOut = () => {
    setShowSignOutModal(false);
  };

  const handleSignInClick = () => {
    setShowSignInModal(true);
  };

  const handleSignInWithX = () => {
    setShowSignInModal(false);
    signInWithTwitter();
  };

  const handleConnectMetaMask = () => {
    setShowSignInModal(false);
    connectWallet('metamask');
  };

  const handleConnectCoinbase = () => {
    setShowSignInModal(false);
    connectWallet('coinbase');
  };

  const handleConnectPhantom = () => {
    setShowSignInModal(false);
    connectWallet('phantom');
  };

  // Detect if we're on staging environment
  const isStaging = () => {
    const hostname = window.location.hostname;
    return hostname.includes('staging');
  };

  // Get display name for user button
  const getDisplayName = () => {
    if (!user) return '';
    if (user.authType === 'twitter' && user.twitterHandle) {
      return user.twitterHandle;
    }
    return user.displayName;
  };

  // Check if user is admin
  const isAdmin = user?.twitterHandle === '_nngmi';

  return (
    <header className={`app-header ${isStaging() ? 'staging' : ''}`} data-testid="app-header">
      <div className="header-container">
        <Link to="/" className="header-logo" onClick={() => { closeMenu(); window.scrollTo(0, 0); }} data-testid="header-logo">
          <img src="/icons/logo-64x64.png" alt="mfers arcade logo" />
          <h1>mfers arcade</h1>
        </Link>

        <div className="header-right">
          {isAuthenticated && user && (
            <button
              className="user-info-outside"
              onClick={handleSignOutClick}
              data-testid="user-info"
            >
              {user.authType === 'twitter' ? (
                <img src="/images/x-logo-black.png" alt="X" className="x-logo-small" />
              ) : (
                <span style={{ marginRight: '4px' }}>◆</span>
              )}
              {getDisplayName()}
            </button>
          )}

          {!isAuthenticated && (
            <button
              className="sign-in-btn-outside"
              onClick={handleSignInClick}
              data-testid="sign-in-btn"
            >
              Sign In
            </button>
          )}

          <button
            className={`hamburger ${isMenuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
            data-testid="hamburger-menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      <nav className={`nav-menu ${isMenuOpen ? 'open' : ''}`} data-testid="nav-menu">
        <div className="menu-content">
          <button
            className="menu-item home-btn"
            onClick={() => handleNavigation('/')}
            data-testid="nav-home"
          >
            Home
          </button>

          <button
            className="menu-item account-btn"
            onClick={() => handleNavigation('/settings')}
            data-testid="nav-account"
          >
            Account
          </button>

          <button
            className="menu-item about-btn"
            onClick={() => handleNavigation('/about')}
            data-testid="nav-about"
          >
            About
          </button>

          {isAdmin && (
            <button
              className="menu-item replay-btn"
              onClick={() => handleNavigation('/replay')}
              data-testid="nav-replay"
            >
              Replays (Admin)
            </button>
          )}

          {isAuthenticated && (
            <button
              className="menu-item sign-out-btn"
              onClick={handleSignOutClick}
              data-testid="sign-out-btn"
            >
              {user?.authType === 'twitter' ? (
                <img src="/images/x-logo-black.png" alt="X" className="x-logo-small" />
              ) : (
                <span style={{ marginRight: '8px' }}>◆</span>
              )}
              Sign Out
            </button>
          )}

        </div>
      </nav>

      {isMenuOpen && <div className="overlay" onClick={closeMenu} data-testid="menu-overlay"></div>}

      {/* Sign Out Confirmation Modal */}
      {showSignOutModal && (
        <div className="modal-overlay" onClick={cancelSignOut} data-testid="signout-modal-overlay">
          <div className="modal-content signout-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Sign Out?</h3>
            <p>Are you sure you want to sign out?</p>
            <div className="modal-buttons">
              <button className="modal-button cancel-button" onClick={cancelSignOut}>
                Cancel
              </button>
              <button className="modal-button confirm-button" onClick={confirmSignOut}>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sign In Modal */}
      {showSignInModal && (
        <div className="modal-overlay" onClick={() => setShowSignInModal(false)} data-testid="signin-modal-overlay">
          <div className="modal-content signin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Sign In</h3>
            <div className="signin-options">
              <button
                className="signin-option-btn twitter-btn"
                onClick={handleSignInWithX}
                disabled={isConnecting}
              >
                <img src="/images/x-logo-black.png" alt="X" className="signin-icon" />
                Sign in with X
              </button>
              <button
                className="signin-option-btn metamask-btn"
                onClick={handleConnectMetaMask}
                disabled={isConnecting}
              >
                <span className="signin-icon">🦊</span>
                MetaMask
              </button>
              <button
                className="signin-option-btn coinbase-btn"
                onClick={handleConnectCoinbase}
                disabled={isConnecting}
              >
                <span className="signin-icon">🔵</span>
                Coinbase
              </button>
              <button
                className="signin-option-btn phantom-btn"
                onClick={handleConnectPhantom}
                disabled={isConnecting}
              >
                <span className="signin-icon">👻</span>
                Phantom
              </button>
            </div>
            <button
              className="modal-button cancel-button"
              onClick={() => setShowSignInModal(false)}
              style={{ marginTop: '16px', width: '100%' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

    </header>
  );
}

export default Header;
